import { styled } from '@linaria/react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PaymentChannel } from '../../types/wallet-management'
import { api } from '../../utils/api-client'
import Spinner from '../common/spinner'
import { TailSpin } from '../loaders'
import AddBankBtn from '../payment-form/add-bank-btn'
import Platform from './platform'
import DepositForm from './deposit-form'
import { useTranslation } from 'react-i18next'
import { numberWithCommas } from '../../utils/number'

const KycBank = lazy(() => import('../../pages/kyc-bank'))
const KycBankList = lazy(() => import('../../pages/kyc-bank-list'))

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.6fr;
`

const ChannelsContainer = styled.div`
  width: 100%;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 4px solid var(--tertiary);
  padding: 48px 0 48px 48px;
  /* width */
  &::-webkit-scrollbar {
    width: 18px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border: 5px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }

  .theme-dark &::-webkit-scrollbar-thumb {
    background-color: #000;
  }

  .theme-light &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }
`

const Loading = styled.div`
  padding: 50px 0;
`

const FormContainer = styled.div`
  width: 100%;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 48px 96px;
  /* width */
  &::-webkit-scrollbar {
    width: 18px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border: 5px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }

  .theme-dark &::-webkit-scrollbar-thumb {
    background-color: #000;
  }

  .theme-light &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }
`

type DepositProps = {
  showWalletManagementModal: (page?: number, tab?: number) => void
  walletManagementDefaultTab?: {
    tab?: number
    page?: number
  }
}

interface PromoCodeResponse {
  data: {
    id: string
    promo_code: string
    spagent_id: string
    content: string
    bonus_amount: number
    total_credit: number
  }
}

export const MinMaxLimit = ({
  minimum,
  maximum,
}: {
  minimum: number
  maximum: number
}) => {
  const { t } = useTranslation('walletManagement')
  return (
    <p className="flex items-center text-sm h-auto">
      {t('min')} <b className="font-Avenir-Heavy ml-1 text-secondary-txt leading-none">${numberWithCommas(minimum)}</b>
      <span className="mx-1 h-3.5 w-[1px] inline-block bg-[#999999]" />
      {t('max')} <b className="font-Avenir-Heavy ml-1 text-secondary-txt leading-none">${numberWithCommas(maximum)}</b>
    </p>
  )
}

const Deposit = ({
  showWalletManagementModal,
  walletManagementDefaultTab,
}: DepositProps) => {
  const selectedPaymentChannel = useBoundStore(
    state => state.payment.selectedPaymentChannel,
  )
  const setSelectedPaymentChannel = useBoundStore(
    state => state.payment.setSelectedPaymentChannel,
  )
  const [isDepositChannelsLoading, setIsDepositChannelsLoading] =
    useState(false)
  const [paymentChannels, setPaymentChannels] = useState<PaymentChannel[]>([])

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    setIsDepositChannelsLoading(true)
    api
      .post<ApiResponse<PaymentChannel[]>>(
        '/payment.php',
        {
          type: 'onlinepay_list_v1',
          payType: 0,
        },
        { signal },
      )
      .then(res => {
        const { status, info } = res.data
        if (status === 1) {
          setSelectedPaymentChannel(info[0])
          setPaymentChannels(info)
        }
        setIsDepositChannelsLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    !selectedPaymentChannel && setSelectedPaymentChannel(paymentChannels[0])
  }, [selectedPaymentChannel])

  return (
    <Grid>
      <ChannelsContainer>
        {isDepositChannelsLoading && (
          <Loading className="flx-ctr">
            <TailSpin width={40} height={40} color="#fff" />
          </Loading>
        )}
        {!isDepositChannelsLoading &&
          paymentChannels.map((item, index) => (
            <Platform
              key={index}
              icon={item.img}
              label={item.show_name}
              route="deposit"
              active={selectedPaymentChannel?.id === item.id}
              onClick={() => {
                showWalletManagementModal(0, 0)
                setSelectedPaymentChannel(item)
              }}
              {...(item.is_show_pay_limit && {
                note: <MinMaxLimit {...item.pay_limit} />,
              })}
            />
          ))}
      </ChannelsContainer>
      <FormContainer>
        {walletManagementDefaultTab?.page === 0 ||
        !walletManagementDefaultTab?.page ? (
          <DepositForm
            addBankBtn={
              selectedPaymentChannel?.need_bank_card === 1 ? (
                <AddBankBtn onClick={() => showWalletManagementModal(1)} />
              ) : null
            }
          />
        ) : walletManagementDefaultTab?.page === 1 ? (
          <Suspense fallback={<Spinner />}>
            <KycBank
              goToBankCards={() => showWalletManagementModal(2)}
              onCancel={() => showWalletManagementModal(0)}
              showWalletManagementModal={showWalletManagementModal}
              method="deposit"
            />
          </Suspense>
        ) : (
          <Suspense fallback={<Spinner />}>
            <KycBankList
              goToKycBank={() => showWalletManagementModal(1)}
              showWalletManagementModal={showWalletManagementModal}
              method="deposit"
            />
          </Suspense>
        )}
      </FormContainer>
    </Grid>
  )
}

export default Deposit
